// =============================================================================
// GROUNDING GAPS — the captured NULLs from the no-invention grounding gate.
//
// CLAUDE.md rule-zero (NO INVENTION, EVER) is enforced in code by the
// GroundingGateService: any value an engine produces about a person that does NOT
// trace to real signal is dropped — and CAPTURED here as a structured, measurable
// gap (never a silent drop). The gaps surface in the corpus interface as enrich
// prompts ("your profile reached for 'X' but nothing grounds it — add it"); the
// user enriches, the corpus grows, the next pass grounds more, null-rate falls. A
// measurable feedback loop.
//
// grounding_runs is the append-only coverage denominator (grounded vs nulled per
// pass). Coverage = grounded / (grounded + nulled), folded into /readiness.
//
// Per-user (text user_id, every query scoped). Additive only (rule #11).
// NB: the captured-null DOMAIN type is `CapturedNull` (apps/api/services/grounding/
// contracts.ts) — NOT `GroundingGap`, which already exists at dna/grounding.ts with
// a different shape (corpus-card gaps for the interview agent).
// =============================================================================

import { index, integer, pgTable, text, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const groundingGaps = pgTable(
  'grounding_gaps',
  {
    id: id(),
    userId: text('user_id').notNull(),
    /** Which generator the gap came from — 'role_match' | 'presence' | 'tailoring' | … */
    surface: varchar('surface', { length: 24 }).notNull(),
    /** The slot the model tried to fill — e.g. 'strong_adjacent' | 'recommendedTitle'. */
    slot: varchar('slot', { length: 40 }).notNull(),
    /** The exact ungrounded string the model produced (so the enrich prompt can name it). */
    droppedValue: text('dropped_value').notNull(),
    /** Why it was nulled — 'no_corpus_trace' | 'evidence_untraceable' |
     *  'in_declared_away_domain' | 'away_suspect_past_role_only'. */
    missingSignal: varchar('missing_signal', { length: 40 }).notNull(),
    /** A short snapshot of the declared direction at capture time (for the prompt). */
    directionContext: text('direction_context'),
    /** Idempotent capture: the same gap re-seen bumps this instead of duplicating. */
    occurrences: integer('occurrences').notNull().default(1),
    /** 'open' until the user enriches enough to ground it, then 'resolved'. */
    status: varchar('status', { length: 16 }).notNull().default('open'),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('grounding_gaps_user_idx').on(t.userId, t.surface),
    // Idempotent natural key — re-capturing the same gap upserts (bump occurrences).
    //   CREATE UNIQUE INDEX IF NOT EXISTS grounding_gaps_key_uq
    //     ON grounding_gaps (user_id, surface, slot, dropped_value);
    keyUq: uniqueIndex('grounding_gaps_key_uq').on(t.userId, t.surface, t.slot, t.droppedValue),
  }),
);

export const groundingRuns = pgTable(
  'grounding_runs',
  {
    id: id(),
    userId: text('user_id').notNull(),
    surface: varchar('surface', { length: 24 }).notNull(),
    /** Candidates that PASSED the gate this pass. */
    grounded: integer('grounded').notNull(),
    /** Candidates DROPPED (and captured) this pass. */
    nulled: integer('nulled').notNull(),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('grounding_runs_user_idx').on(t.userId, t.surface),
  }),
);
